import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  QrCode2 as QrCode2Icon,
  TableRestaurant as TableRestaurantIcon,
  Inventory2 as Inventory2Icon,
  BarChart as BarChartIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingBagOutlined as ShoppingBagOutlinedIcon,
  Payments as PaymentsIcon,
  WhatsApp as WhatsAppIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Star as StarIcon,
  RestaurantMenu as RestaurantMenuIcon,
  LocalOffer as LocalOfferIcon,
  ReceiptLong as ReceiptLongIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CallToAction from "../components/CallToAction";
import PageBanner from "../components/PageBanner";
import PageTitle from "../components/PageTitle";
import featuresBanner from "../assets/images/features.avif";

const Features = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const features = [
    {
      icon: <QrCode2Icon sx={{ fontSize: 40 }} />,
      title: "QR-Based Ordering",
      desc: "Generate QR codes for tables. Customers scan to view menu & order instantly.",
      category: "For Restaurants",
    },
    {
      icon: <TableRestaurantIcon sx={{ fontSize: 40 }} />,
      title: "Digital Menu Management",
      desc: "Create categories, add items with images, prices, and stock tracking.",
      category: "For Restaurants",
    },
    {
      icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 40 }} />,
      title: "Multi-Order Types",
      desc: "Support DINE_IN, TAKEAWAY, and DELIVERY orders with ease.",
      category: "For Restaurants",
    },
    {
      icon: <PaymentsIcon sx={{ fontSize: 40 }} />,
      title: "Flexible Payments",
      desc: "Cash, UPI, Card - payments go directly to restaurant's account.",
      category: "For Restaurants",
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 40 }} />,
      title: "WhatsApp Integration",
      desc: "Orders automatically sent to restaurant's WhatsApp for instant alerts.",
      category: "Integration",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: "Staff Management",
      desc: "Manage staff, assign roles, and track performance.",
      category: "For Restaurants",
    },
    {
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      title: "Real-Time Dashboard",
      desc: "Track orders, inventory, and analytics in real-time.",
      category: "For Restaurants",
    },
    {
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      title: "Customer Ratings",
      desc: "Customers can rate restaurants and leave feedback.",
      category: "For Customers",
    },
    {
      icon: <RestaurantMenuIcon sx={{ fontSize: 40 }} />,
      title: "Digital Menu for Customers",
      desc: "Customers view full menu with images, prices & stock status.",
      category: "For Customers",
    },
    {
      icon: <LocalOfferIcon sx={{ fontSize: 40 }} />,
      title: "Offers & Deals",
      desc: "Create and manage promotional offers and discounts.",
      category: "For Restaurants",
    },
    {
      icon: <Inventory2Icon sx={{ fontSize: 40 }} />,
      title: "Inventory Management",
      desc: "Track stock levels and get low stock alerts.",
      category: "For Restaurants",
    },
    {
      icon: <ReceiptLongIcon sx={{ fontSize: 40 }} />,
      title: "Smart Billing System",
      desc: "Auto-generate bills with 30-day trial and grace period.",
      category: "For Restaurants",
    },
  ];

  const customerFeatures = [
    "No App Required - Just Scan QR Code",
    "No Registration Needed",
    "View Menu with Prices & Images",
    "Check Stock Availability",
    "Multiple Order Types",
    "Flexible Payment Options",
    "Rate & Review Restaurants",
  ];

  return (
    <>
      <PageTitle
        title="RestaurantWala Features – Complete Restaurant Management Solution"
        description="Explore RestaurantWala features including QR digital menu, order management, smart billing, staff management, tables, offers, inventory and analytics."
        keywords="restaurant management features, restaurant software features, QR menu system, restaurant billing, restaurant order management"
      />
      <Navbar />
      <PageBanner
        title="Everything You Need to Run Your Restaurant"
        subtitle="All-in-one platform to manage orders, inventory, tables, staff, and customers"
        chipLabel="FEATURES"
        bgImage={featuresBanner}
      />

      <Box sx={{ bgcolor: "#ffffff", py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Features Grid */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "var(--shadow-orange)",
                      borderColor: "var(--primary)",
                    },
                  }}
                >
                  <Chip
                    label={feature.category}
                    size="small"
                    sx={{
                      bgcolor: "var(--bg-primary-soft)",
                      color: "var(--primary)",
                      fontSize: "10px",
                      fontWeight: 700,
                      mb: 2,
                      borderRadius: "8px",
                    }}
                  />
                  <Box sx={{ color: "var(--primary)", mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "var(--text)",
                      mb: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "var(--text-light)",
                      fontSize: "14px",
                      lineHeight: 1.6,
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* For Customers Section */}
          <Box sx={{ mt: { xs: 4, md: 6 } }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: "24px",
                bgcolor: "var(--bg-light)",
                border: "1px solid var(--border)",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "var(--text)",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                For{" "}
                <Box component="span" sx={{ color: "var(--primary)" }}>
                  Customers
                </Box>
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                  color: "var(--text-light)",
                  mb: 3,
                }}
              >
                No registration needed! Just scan QR code and start ordering
              </Typography>
              <Grid container spacing={2}>
                {customerFeatures.map((feature, index) => (
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: "12px",
                        bgcolor: "#ffffff",
                        border: "1px solid var(--border)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "var(--primary)",
                          bgcolor: "var(--bg-primary-soft)",
                        },
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ color: "var(--primary)", fontSize: 20 }}
                      />
                      <Typography
                        sx={{ fontSize: "14px", color: "var(--text)" }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        </Container>
      </Box>

      <CallToAction />
      <Footer />
    </>
  );
};

export default Features;
